import * as moment from "moment";
import { DatexPipe } from "../pipes/datex/datex";

import { EventTimeSlots } from "./event-timeslots";

import { config } from "src/environments/config";

export class ParticipantTimeslot {
  id: number;
  participant_id: number;
  created_at: string;
  date_created_at: Date = new Date();
  updated_at: string;
  date_updated_at: Date = new Date();

  // object with actual structure -> create model...
  timeslots: {} = {};
  // array with allowed timeslots duration
  durations = [];
  // default size of block in minutes
  timeslot_length: 5;

  /**
   * constructor
   *
   * @return void
   */
  public constructor(init?: ParticipantTimeslot) {
    if (!!init) {
      Object.assign(this, init);
      this.timeslot_length = init.timeslots['timeslot_length'];
      this.date_created_at = moment(this.created_at, 'YYYY-MM-DD HH:mm:ss').toDate();
    }
    else {
      const datePipe = new DatexPipe();
      this.created_at = datePipe.transform(this.date_created_at, 'yyyy-MM-dd HH:mm:ss');
      this.updated_at = datePipe.transform(this.date_updated_at, 'yyyy-MM-dd HH:mm:ss');
    }

    // generate durations, maximum size for appointment request
    let duration = this.timeslot_length;
    while (duration <= config.maxAppointmentBlockSize) {
      this.durations.push(duration);
      duration += this.timeslot_length;
    }
  }

    /**
      * Load setting from event timeslots
      *
      * @param {EventTimeSlots} eventTimeslots
      */
    public loadFromEvent(eventTimeslots: EventTimeSlots){
        this.timeslot_length = eventTimeslots.timeslot_length;
        this.durations = eventTimeslots.durations;
        this.timeslots['items'] = eventTimeslots.eventTimeslots;
        this.timeslots['timeslot_length'] = eventTimeslots.timeslot_length;
    }

  /**
   * Generate array with day timestamps by block keys
   *
   * @return number[]
   */
  public getDays(past = true) {
    if (this.timeslots['items']) {
      if (past) {
        return Object.keys(this.timeslots['items']).map(day => new Date(Number(day)));
      } else {
        let today = moment(Date.now()).set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
        return Object.keys(this.timeslots['items'])
            .filter(day => { return moment(Number(day)).set({ hour: 0, minute: 0, second: 0, millisecond: 0 }).valueOf() >= today.valueOf() })
            .map(day => new Date(Number(day)));
      }
    } else {
      return [];
    }
  }

    /**
      * Generate dayily offset for first hour
      *
      * @return number[]
      */
    public getDayilyOffset() {
        if (this.timeslots['items']) {
            let dailyOffset = [];
            Object.keys(this.timeslots['items']).forEach((day) => {
                // get first time of the day
                if (this.timeslots['items'][day][0]) {
                    let firstTime = moment(this.timeslots['items'][day][0].start);
                    if (firstTime.minutes()) {
                        dailyOffset[day] = firstTime.minutes() / this.timeslot_length;
                    } else {
                        dailyOffset[day] = 0;
                    }
                }
            });
            return dailyOffset;
        } else {
            return [];
        }
    }

  /**
   * Find proper slots for participant appointments and mark them as blocked
   *
   * @param appointments array of participant appointments
   * @return void
   */
  public markAppointment(appointments, status?: string): void {
    if (appointments) {
      appointments.forEach((appointment) => {
        // now only count with confirmed appointments, appointment without status are from API which is returning only confirmed
        if (!appointment.status || appointment.status == 4 || appointment.status == 1) {
          // prepare date and times from appointment
          let day = moment(appointment.starts_at, 'YYYY-MM-DD HH:mm:ss').set({ hour: 0, minute: 0, second: 0, millisecond: 0 }).toDate();
          let starts_at = moment(appointment.starts_at, 'YYYY-MM-DD HH:mm:ss').set({ second: 0, millisecond: 0 });
          let date_ends_at;
          if (!appointment.ends_at) {
            date_ends_at = moment(appointment.starts_at, 'YYYY-MM-DD HH:mm:ss').set({ second: 0, millisecond: 0 }).add(this.timeslots['timeslot_length'], 'minutes').toDate();
          } else {
            date_ends_at = moment(appointment.ends_at, 'YYYY-MM-DD HH:mm:ss').set({ second: 0, millisecond: 0 }).toDate();
          }

          let date_starts_at = starts_at.toDate();

          // check only against existing days
          if (this.timeslots['items'][day.getTime()]) {
            this.timeslots['items'][day.getTime()].forEach((slot, index) => {
              // transform times and round them to minutes
              slot.start = moment(slot.start).set({ second: 0, millisecond: 0 });
              slot.end = moment(slot.end).set({ second: 0, millisecond: 0 });
              // check if there is a time collision
              if ((slot.start.toDate() >= date_starts_at && slot.end.toDate() <= date_ends_at)
                || (slot.start.toDate() < date_ends_at && slot.start.toDate() >= date_starts_at && slot.end.toDate() > date_ends_at)) {
                // mark proper timeslots as timeslot with appointment
                if (!status) {
                  this.timeslots['items'][day.getTime()][index].appointment = appointment;
                } else {
                  this.timeslots['items'][day.getTime()][index].status = status;
                }
              }
            });
          }
        }
      })
    }
  }

  /**
   * Find same slots for participant timeslots and mark them as blocked
   *
   * @param participantTimeslot
   */
  public markSlot(participantTimeslot) {
    if (participantTimeslot) {
      // compare orther slots with users and mark blocked if there is no free slot
      Object.keys(this.timeslots['items']).forEach((key) => {
        this.timeslots['items'][key].forEach((slot, index) => {
          if (participantTimeslot[key] && participantTimeslot[key][index]
            && participantTimeslot[key][index].status != 'free') {
            this.timeslots['items'][key][index].status = 'blocked';
          } else {
            // check past time and block slots if they are in past
            if (moment(this.timeslots['items'][key][index].start).valueOf() < Date.now()) {
              this.timeslots['items'][key][index].status = 'blocked';
            }
          }
        })
      });
    }
  }

  /**
   * Generate array of pripate meetings - where slots are blocked...
   * It will join blocked slots in rows and also separate by free blocks or blocks with notes
   *
   * @return privateAppointments
   */
  generatePrivateAppointment() {
    let privateAppointments = [];
    let privateAppointment;
    Object.keys(this.timeslots['items']).forEach((key) => {
      this.timeslots['items'][key].forEach((slot, index) => {
        if (slot.status != 'free' && !slot.appointment && !slot.note && privateAppointment) {
          // update end time for block
          privateAppointment.ends_at = moment(slot.end).set({ second: 0, millisecond: 0 });
          privateAppointment.date_ends_at = moment(slot.end).set({ second: 0, millisecond: 0 }).toDate();
        } else {
          // end up actual joined block and put it into array
          if (privateAppointment) {
            privateAppointments.push({ ...privateAppointment });
            privateAppointment = null;
          }
          // create new private meeting
          if (slot.status == 'blocked' && !slot.appointment) {
            privateAppointment = {
              starts_at: moment(slot.start).set({ second: 0, millisecond: 0 }).format('YYYY-MM-DD HH:mm:ss'),
              ends_at: moment(slot.end).set({ second: 0, millisecond: 0 }).format('YYYY-MM-DD HH:mm:ss'),
              date_starts_at: moment(slot.start).set({ second: 0, millisecond: 0 }).toDate(),
              date_ends_at: moment(slot.end).set({ second: 0, millisecond: 0 }).toDate(),
              status: 4,
              description: slot.note
            }
          }
        }
      });
      if (privateAppointment) {
        privateAppointments.push({ ...privateAppointment });
        privateAppointment = null; // reset slot for every day
      }
    });

    return privateAppointments;
  }
}
